import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import mammoth from "mammoth";
import { useLanguage } from "../contexts/LanguageContext";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import ScheduleInterviewDialog from "../components/ScheduleInterviewDialog";
import AddIntervieweeDialog from "../components/AddIntervieweeDialog";
import RecordingDialog from "../components/RecordingDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Mic,
  Upload,
  CalendarDays,
  Paperclip,
  ArrowLeft,
  Loader2,
  Download,
  Trash2,
  Eye,
  DockIcon,
  File,
  Trash,
} from "lucide-react";
import { getOutputDocuments } from "../data/assessments";
import { useAssessments } from "../contexts/AssessmentContext";
import FullScreenLoader from "../components/FullScreenLoader";
import {
  Dialog as MiniDialog,
  DialogContent as MiniDialogContent,
  DialogHeader as MiniDialogHeader,
  DialogTitle as MiniDialogTitle,
} from "../components/ui/dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const ASSESSMENT_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/assessment";
const ASSEMBLYAI_API_KEY = "8a0cd1b76cb54e31926c3756a0665ad7";
const POLICY_TIMER_KEY = "bv.aiPolicyPoll";
const ASSESS_TIMER_KEY = "bv.assessmentPoll";

const storeTimer = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {}
};

const clearTimerStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

function getInterviewBadgeClass(status) {
  switch (status) {
    case "completed":
      return "bg-green-500 hover:bg-green-500/80 text-white border-transparent";
    case "scheduled":
      return "bg-muted text-black";
    case "pending":
      return "bg-amber-100 text-black border-amber-200";
    default:
      return "";
  }
}

const AssessmentNewV1 = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { assessments } = useAssessments();
  const { t } = useLanguage();
  const getStatusLabel = useCallback(
    (status) => {
      const normalized = String(status || "").toLowerCase();
      switch (normalized) {
        case "completed":
          return t("status.completed");
        case "ai-workflow-completed":
          return t("status.aiWorkflowCompleted");
        case "ai-policies-completed":
          return t("status.aiPoliciesCompleted");
        case "active":
          return t("status.active");
        default:
          return status || "";
      }
    },
    [t],
  );
  const [apiAssessment, setApiAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadingFor, setUploadingFor] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [recordingFor, setRecordingFor] = useState(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [processingModal, setProcessingModal] = useState({
    open: false,
    seconds: 0,
    title: "Processing",
  });

  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [policyTimer, setPolicyTimer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(POLICY_TIMER_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [assessTimer, setAssessTimer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(ASSESS_TIMER_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [policyElapsed, setPolicyElapsed] = useState({
    minutes: 0,
    seconds: 0,
  });
  const [assessElapsed, setAssessElapsed] = useState({
    minutes: 0,
    seconds: 0,
  });

  const authToken = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.authToken || null;
    } catch {
      return null;
    }
  }, []);
  const companyLogo = useMemo(() => {
    try {
      const raw = localStorage.getItem("bv.registerResponse");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return (
        parsed?.companyLogo || parsed?.logo || parsed?.user?.companyLogo || ""
      );
    } catch {
      return "";
    }
  }, []);

  const fetchAssessment = useCallback(async () => {
    setError("");
    if (!assessmentId) return;
    if (!authToken) {
      navigate("/");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(ASSESSMENT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: authToken,
        },
        body: JSON.stringify({ assessmentId: assessmentId }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success === false) {
        navigate("/");
        return;
      }
      if (!res.ok) {
        throw new Error(data?.message || `Request failed (${res.status})`);
      }
      const companyDetails =
        data?.companyDetails || data?.assessment || data || {};
      const intervieweeList = Array.isArray(data?.intervieweeList)
        ? data.intervieweeList
        : Array.isArray(companyDetails?.interviewees)
          ? companyDetails.interviewees
          : [];

      const interviewees = intervieweeList.map((i) => {
        const files = Array.isArray(i.files) ? i.files : [];
        const transcriptText =
          files.find((f) => f?.transcript?.text)?.transcript?.text ||
          files.find((f) => f?.transcript)?.transcript?.text ||
          files.find((f) => f?.url)?.url ||
          null;

        return {
          name: i.name || "Interviewee",
          role: i.position || i.department || i.role || "",
          intervieweeId: i?.intervieweeId,
          type:
            i.aiPolocyGuidelines === "Yes" ? "Policies" : i.type || "Workflow",
          interview: i.interviewStatus || i.interview || "pending",
          transcript: transcriptText,
          files,
          actions: Array.isArray(i.actions) ? i.actions : [],
        };
      });

      const total = interviewees.length || 0;
      const withFiles = interviewees.filter(
        (i) => Array.isArray(i.files) && i.files.length > 0,
      ).length;
      const progress = total === 0 ? 0 : Math.round((withFiles / total) * 100);

      setApiAssessment({
        ...companyDetails,
        id: String(
          companyDetails.assessmentId || companyDetails._id || assessmentId,
        ),
        assessmentId: companyDetails.assessmentId || assessmentId,
        company:
          companyDetails.companyName || companyDetails.title || "Untitled",
        contact: companyDetails.keyContact || companyDetails.contact || "",
        contactTitle:
          companyDetails.position || companyDetails.contactTitle || "",
        email: companyDetails.email || "",
        phone: companyDetails.phone || "",
        status: (companyDetails.status || "active").toString(),
        progress,
        interviewees,
        assessmentFile: companyDetails.assessmentFile,
        assessmentAiPolicyPdf: companyDetails.assessmentAiPolicyPdf,
        aiAssessmentCompleted: companyDetails.aiAssessmentCompleted === true,
        aiPolicyAssessmentCompleted:
          companyDetails.aiPolicyAssessmentCompleted === true,
      });
    } catch (e) {
      setError(e.message || "Failed to fetch assessment.");
    } finally {
      setLoading(false);
    }
  }, [assessmentId, authToken, navigate]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  const assessment =
    apiAssessment ||
    assessments.find(
      (a) =>
        a.assessmentId === assessmentId ||
        String(a.assessmentId) === String(assessmentId) ||
        a.id === assessmentId,
    );
  const interviewees = useMemo(
    () =>
      Array.isArray(assessment?.interviewees) ? assessment.interviewees : [],
    [assessment],
  );

  const getDocumentSubtitle = useCallback(
    (doc) => {
      const totalCount = interviewees.length;
      const policyInterviewees = interviewees.filter(
        (i) => i.type?.toString().toLowerCase() === "policies",
      );
      const totalPolicyCount = policyInterviewees.length;
      const completedPolicyWithTranscripts = policyInterviewees.filter(
        (i) => i.interview === "completed" && i.transcript,
      ).length;
      const allCompleted = interviewees.every(
        (i) => i.interview === "completed" && i.transcript,
      );
      const anyPolicyCompleted = completedPolicyWithTranscripts >= 1;

      if (doc.title === "AI Policies & Guidelines") {
        return anyPolicyCompleted
          ? `${completedPolicyWithTranscripts}/${totalPolicyCount} ${t(
              "dashboard.complete",
            )} · ${t("Ready to generate")}`
          : `${totalPolicyCount} ${t("interviewee(s)")} · ${t(
              "At least 1 interview with transcript required",
            )}`;
      }

      return allCompleted
        ? `${totalCount} ${t("interviewee(s)")} · ${t(
            "All interviews complete",
          )}`
        : `${totalCount} ${t("interviewee(s)")} · ${t(
            "Complete all interviews & upload transcripts first",
          )}`;
    },
    [interviewees, t],
  );

  const aiAssessmentCompleted = assessment?.aiAssessmentCompleted === true;
  const aiPolicyAssessmentCompleted =
    assessment?.aiPolicyAssessmentCompleted === true;

  const allHaveFiles = useMemo(
    () =>
      interviewees.length > 0 &&
      interviewees.every((i) => Array.isArray(i.files) && i.files.length > 0),
    [interviewees],
  );

  const allHaveFilesReadiness = useMemo(() => {
    const readinessInterviewees = interviewees.filter(
      (i) => i.type !== "Policies",
    );
    return (
      readinessInterviewees.length > 0 &&
      readinessInterviewees.every(
        (i) => Array.isArray(i.files) && i.files.length > 0,
      )
    );
  }, [interviewees]);

  const allHaveFilesPolicy = useMemo(() => {
    const policyInterviewees = interviewees.filter(
      (i) => i.type === "Policies",
    );
    return (
      policyInterviewees.length > 0 &&
      policyInterviewees.every(
        (i) => Array.isArray(i.files) && i.files.length > 0,
      )
    );
  }, [interviewees]);

  // Re-hydrate timers when returning to page for same company

  // Timer tickers for policy/assessment generation
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      if (policyTimer?.startedAt) {
        const diff = Math.max(0, now - policyTimer.startedAt);
        setPolicyElapsed({
          minutes: Math.floor(diff / 60000),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
      if (assessTimer?.startedAt) {
        const diff = Math.max(0, now - assessTimer.startedAt);
        setAssessElapsed({
          minutes: Math.floor(diff / 60000),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
  }, [policyTimer, assessTimer]);

  const clearTimer = useCallback((key, setter) => {
    clearTimerStorage(key);
    setter(null);
  }, []);

  // Background poll every 10s until files appear
  useEffect(() => {
    if (!policyTimer?.startedAt && !assessTimer?.startedAt) return;
    let stopped = false;
    const poll = async () => {
      if (!authToken) return;
      try {
        const assessIdPayload =
          assessment?.assessmentId ||
          apiAssessment?.assessmentId ||
          assessmentId;
        if (!assessIdPayload) return;
        const res = await fetch(ASSESSMENT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({ assessmentId: assessIdPayload }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) return;
        const companyDetails =
          data?.companyDetails || data?.assessment || data || {};
        const hasAssess = !!companyDetails.assessmentFile;
        const hasPolicy = !!companyDetails.assessmentAiPolicyPdf;
        if (hasAssess) clearTimer(ASSESS_TIMER_KEY, setAssessTimer);
        if (hasPolicy) clearTimer(POLICY_TIMER_KEY, setPolicyTimer);
        if (hasAssess || hasPolicy) {
          setApiAssessment((prev) =>
            prev
              ? {
                  ...prev,
                  ...companyDetails,
                  assessmentFile:
                    companyDetails.assessmentFile || prev.assessmentFile,
                  assessmentAiPolicyPdf:
                    companyDetails.assessmentAiPolicyPdf ||
                    prev.assessmentAiPolicyPdf,
                }
              : prev,
          );
        }
      } catch (e) {
        // swallow errors, keep polling
      }
    };

    poll();
    const id = setInterval(() => {
      if (!stopped) poll();
    }, 5000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [
    policyTimer?.startedAt,
    assessTimer?.startedAt,
    authToken,
    assessmentId,
    clearTimer,
    fetchAssessment,
  ]);
  const startPolicyPolling = useCallback(() => {
    if (!assessment?.company) return;
    const payload = { startedAt: Date.now(), companyName: assessment.company };
    setPolicyTimer(payload);
    storeTimer(POLICY_TIMER_KEY, payload);
  }, [assessment?.company]);

  const startAssessPolling = useCallback(() => {
    if (!assessment?.company) return;
    const payload = { startedAt: Date.now(), companyName: assessment.company };
    setAssessTimer(payload);
    storeTimer(ASSESS_TIMER_KEY, payload);
  }, [assessment?.company]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleInterviewee, setScheduleInterviewee] = useState("");
  const [assetMenuOpen, setAssetMenuOpen] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInterviewee, setEditInterviewee] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editPolicies, setEditPolicies] = useState("No");
  const [savingEdit, setSavingEdit] = useState(false);
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [recordingPerson, setRecordingPerson] = useState({
    name: "",
    type: "",
    data: null,
  });
  const [recordChoiceOpen, setRecordChoiceOpen] = useState(false);
  const getIntervieweeKey = useCallback((inter) => {
    if (!inter) return null;
    return (
      inter.intervieweeId ||
      inter._id ||
      inter.id ||
      inter.email ||
      inter.name ||
      null
    );
  }, []);
  const recordInterRef = useRef(null);
  const activeRecordingInterRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const recordingTargetId = getIntervieweeKey(recordingPerson.data);
  const isRecordingActive =
    recordingTargetId && recordingFor === recordingTargetId;
  const elapsedForDialog = isRecordingActive ? recordSeconds : 0;
  const [docStates, setDocStates] = useState({});
  const [transcriptDialog, setTranscriptDialog] = useState({
    open: false,
    text: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
    });
  }, []);
  const handleConfirmAction = useCallback(async () => {
    const action = confirmDialog.onConfirm;
    try {
      if (typeof action === "function") {
        await action();
      }
    } catch (err) {
      setAlertDialog({
        open: true,
        title: t("Action failed"),
        message: err?.message || t("Unable to complete the action."),
      });
    } finally {
      closeConfirmDialog();
    }
  }, [closeConfirmDialog, confirmDialog.onConfirm, t]);

  const CLOUD_NAME = "dcfotquxt";
  const UPLOAD_PRESET = "flsdzoc2";
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const processingTimerRef = useRef(null);
  const [addingInterviewee, setAddingInterviewee] = useState(false);

  const loadCloudinaryWidget = useCallback(() => {
    if (typeof window === "undefined")
      return Promise.reject(new Error("No window"));
    if (window.cloudinary?.createUploadWidget) {
      return Promise.resolve(window.cloudinary);
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://widget.cloudinary.com/v2.0/global/all.js";
      script.async = true;
      script.onload = () => {
        if (window.cloudinary?.createUploadWidget) {
          resolve(window.cloudinary);
        } else {
          reject(new Error("Cloudinary widget failed to load."));
        }
      };
      script.onerror = () =>
        reject(new Error("Cloudinary widget failed to load."));
      document.body.appendChild(script);
    });
  }, []);

  const patchIntervieweeFiles = async ({
    assessmentId,
    intervieweeId,
    files,
  }) => {
    setLoading(true);
    try {
      await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({
            assessmentId,
            intervieweeId,
            files,
            interviewStatus: files?.length > 0 ? "completed" : "pending",
          }),
        },
      );
      await fetchAssessment();
    } catch (e) {
      setError(e.message || "Failed to update interviewee files.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPdf = async (inter) => {
    const assessmentIdVal = assessment?.assessmentId;
    const intervieweeId = inter?.intervieweeId || inter?._id || inter?.id;
    if (!authToken || !assessmentIdVal || !intervieweeId) {
      setError("Missing identifiers or authorization.");
      return;
    }
    try {
      setUploadingFor(intervieweeId);
      const cloudinary = await loadCloudinaryWidget();
      const widget = cloudinary.createUploadWidget(
        {
          cloudName: CLOUD_NAME,
          uploadPreset: UPLOAD_PRESET,
          sources: ["local", "url"],
          multiple: false,
          resourceType: "raw",
          clientAllowedFormats: ["pdf", "docx", "doc"],
          maxFiles: 1,
          folder: `BizVantage/${assessmentIdVal || "unknown"}/${intervieweeId || "unknown"}/Files`,
        },
        async (error, result) => {
          if (error) {
            setError(error.message || "Upload failed.");
            setUploadingFor(null);
            return;
          }
          if (result?.event === "success" && result?.info) {
            const info = result.info;
            try {
              setLoading(true);
              let fullText = "";
              const isDocx =
                info?.secure_url?.toLowerCase().endsWith(".docx") ||
                info?.format === "docx";

              if (isDocx) {
                try {
                  const res = await fetch(info.secure_url);
                  const buf = await res.arrayBuffer();
                  const docxText = await mammoth.extractRawText({
                    arrayBuffer: buf,
                  });
                  fullText = docxText?.value || "";
                } catch (docErr) {
                  console.warn("DOCX extraction failed:", docErr);
                  fullText = "";
                }
              } else {
                // PDF extraction
                const loadingTask = pdfjsLib.getDocument(info?.secure_url);
                const pdf = await loadingTask.promise;
                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                  const page = await pdf.getPage(pageNum);
                  const content = await page.getTextContent();
                  const strings = content.items.map((item) => item.str);
                  fullText += strings.join(" ") + "\n\n";
                }
              }

              const currentFiles = Array.isArray(inter.files)
                ? inter.files
                : [];
              const newFiles = [
                ...currentFiles,
                {
                  url: info.secure_url,
                  format: info.resource_type || info.format,
                  bytes: info.bytes,
                  public_id: info.public_id,
                  transcript: { text: fullText.trim() },
                },
              ];
              await patchIntervieweeFiles({
                assessmentId: assessmentIdVal,
                intervieweeId,
                files: newFiles,
              });
              await fetchAssessment();
            } catch (err) {
              alert(err);
              setError(err.message || "Unable to extract text from file.");
            } finally {
              setLoading(false);
              setUploadingFor(null);
            }
          } else {
            setUploadingFor(null);
          }
        },
      );
      widget.open();
    } catch (e) {
      setError(e.message || "Upload failed.");
      setUploadingFor(null);
    }
  };

  const deleteIntervieweeConfirmed = async (inter) => {
    if (!authToken) {
      setError("Not authorized.");
      return;
    }
    const assessmentIdVal = assessment?.assessmentId;
    const intervieweeId = inter?.intervieweeId || inter?._id || inter?.id;
    if (!assessmentIdVal || !intervieweeId) {
      setError("Missing identifiers to delete interviewee.");
      return;
    }
    try {
      setDeletingId(intervieweeId);
      await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({
            assessmentId: assessmentIdVal,
            intervieweeId,
            name: inter?.name,
            email: inter?.email,
          }),
        },
      );
      await fetchAssessment();
    } catch (e) {
      setError(e.message || "Failed to delete interviewee.");
    } finally {
      setDeletingId(null);
    }
  };
  const handleDeleteInterviewee = (inter) => {
    setConfirmDialog({
      open: true,
      title: "Delete Interviewee",
      message: `Are you sure you want to delete ${
        inter?.name || "this interviewee"
      }? This action cannot be undone.`,
      onConfirm: () => deleteIntervieweeConfirmed(inter),
    });
  };

  const handleAddInterviewees = async (list) => {
    if (!authToken) {
      setError("Not authorized.");
      return;
    }
    const assessmentIdVal = assessment?.assessmentId;
    if (!assessmentIdVal) {
      setError("Missing assessment id.");
      return;
    }
    const payloads = list.map((i) => ({
      assessmentId: assessmentIdVal,
      name: i.name?.trim(),
      email: i.email?.trim(),
      department: i.department || i.role || "",
      position: i.position || "",
      aiPolocyGuidelines: i.aiPolocyGuidelines || "No",
    }));

    for (const body of payloads) {
      if (!body.name || !body.position) {
        setError("Name and Position are required.");
        return;
      }
    }

    try {
      setAddingInterviewee(true);
      for (const body of payloads) {
        await fetch(
          "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              authorization: authToken,
            },
            body: JSON.stringify(body),
          },
        );
      }
      await fetchAssessment();
    } catch (e) {
      setError(e.message || "Failed to add interviewee(s).");
    } finally {
      setAddingInterviewee(false);
    }
  };

  const handleSaveEditInterviewee = useCallback(async () => {
    if (!authToken) {
      setError("Not authorized.");
      return;
    }
    const assessmentIdVal = assessment?.assessmentId;
    const intervieweeId =
      editInterviewee?.intervieweeId ||
      editInterviewee?._id ||
      editInterviewee?.id;
    if (!assessmentIdVal || !intervieweeId) {
      setError("Missing identifiers to save interviewee.");
      return;
    }
    if (!editName.trim() || !editPosition.trim()) {
      setError("Name and Position are required.");
      return;
    }
    try {
      setSavingEdit(true);
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/interviewees",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify({
            assessmentId: assessmentIdVal,
            intervieweeId,
            name: editName.trim(),
            position: editPosition.trim(),
            department: editPosition.trim(),
            aiPolocyGuidelines: editPolicies,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to save interviewee.");
      }
      await fetchAssessment();
      setEditOpen(false);
      setEditInterviewee(null);
    } catch (e) {
      setError(e.message || "Failed to save interviewee.");
    } finally {
      setSavingEdit(false);
    }
  }, [
    authToken,
    assessment?.assessmentId,
    editInterviewee,
    editName,
    editPosition,
    editPolicies,
    fetchAssessment,
  ]);

  const uploadRecordingToCloudinary = async (blob, inter, key) => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const assessmentIdVal =
      inter?.assessmentId ||
      assessment?.assessmentId ||
      apiAssessment?.assessmentId;
    const intervieweeId =
      getIntervieweeKey(inter) ||
      recordingFor ||
      getIntervieweeKey(recordingPerson.data);
    if (!assessmentIdVal || !intervieweeId) {
      setAlertDialog({
        open: true,
        title: "Upload failed",
        message: "Missing assessment or interviewee identifiers for upload.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", blob, `${Date.now()}.webm`);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append(
      "folder",
      `BizVantage/${assessmentIdVal || "unknown"}/${intervieweeId || "unknown"}/Recordings`,
    );

    // 1) Upload to Cloudinary
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/video/upload`,
      {
        method: "POST",
        body: formData,
      },
    );
    const data = await res.json();
    if (!res.ok || !data.secure_url) {
      throw new Error(data?.error?.message || "Cloudinary upload failed");
    }

    const fileMeta = {
      url: data.secure_url,
      format: data.format,
      bytes: data.bytes,
      public_id: data.public_id,
      transcript: null,
    };

    // 2) Send to AssemblyAI for speech-to-text
    try {
      const createTranscriptRes = await fetch(
        "https://api.assemblyai.com/v2/transcript",
        {
          method: "POST",
          headers: {
            authorization: ASSEMBLYAI_API_KEY,
            "content-type": "application/json",
          },
          body: JSON.stringify({
            audio_url: data.secure_url,
          }),
        },
      );
      const transcriptJob = await createTranscriptRes.json();
      if (!createTranscriptRes.ok || !transcriptJob.id) {
        throw new Error(transcriptJob.error || "Failed to create transcript");
      }

      let transcriptText = null;
      let status = transcriptJob.status;
      const transcriptId = transcriptJob.id;
      const maxAttempts = 20;
      let attempts = 0;

      while (
        (status === "queued" || status === "processing") &&
        attempts < maxAttempts
      ) {
        attempts += 1;
        await sleep(5000);
        const pollRes = await fetch(
          `https://api.assemblyai.com/v2/transcript/${transcriptId}`,
          {
            method: "GET",
            headers: {
              authorization: ASSEMBLYAI_API_KEY,
            },
          },
        );
        const pollData = await pollRes.json();
        status = pollData.status;
        if (status === "completed") {
          transcriptText = pollData;
          break;
        }
        if (status === "error") {
          throw new Error(pollData.error || "Transcription failed");
        }
      }

      if (!transcriptText && attempts >= maxAttempts) {
        throw new Error("Transcription polling timed out.");
      }

      if (transcriptText) {
        fileMeta.transcript = transcriptText;
      }
    } catch (err) {
      // keep upload but surface transcript failure
      setAlertDialog({
        open: true,
        title: "Transcript failed",
        message: err.message || "Transcription failed.",
      });
    }

    // 3) Patch backend with file (and transcript if available)
    const currentFiles = Array.isArray(inter.files) ? inter.files : [];
    await patchIntervieweeFiles({
      assessmentId: assessmentIdVal,
      intervieweeId,
      files: [...currentFiles, fileMeta],
    });

    setAlertDialog({
      open: true,
      title: "Recording Uploaded",
      message: "Audio interview uploaded successfully.",
    });

    await fetchAssessment();
    setRecordingOpen(false);
    setLoading(false);
    return fileMeta;
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        setError(e.message || "Failed to stop recording.");
      }
      mediaRecorderRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecordingFor(null);
    setRecordSeconds(0);
  }, [setError]);

  const startRecording = useCallback(
    async (inter, micOnly = false) => {
      let displayStream = null;
      let micStream = null;
      let audioCtx = null;
      let mixedStream = null;
      try {
        const assessmentIdVal = assessment?.assessmentId;
        const intervieweeId = getIntervieweeKey(inter);
        if (!assessmentIdVal || !intervieweeId) {
          throw new Error("Missing assessment or interviewee id.");
        }

        // Capture microphone always
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        micStreamRef.current = micStream;

        if (micOnly) {
          mixedStream = micStream;
        } else {
          // Ask the user to share a tab/window/screen with system audio enabled.
          displayStream = await navigator.mediaDevices.getDisplayMedia({
            audio: true,
            video: true, // required by getDisplayMedia
          });
          // Stop video tracks (we only need audio).
          displayStream.getVideoTracks().forEach((t) => t.stop());
          displayStreamRef.current = displayStream;

          // Mix system audio + mic into a single audio stream.
          audioCtx = new AudioContext();
          audioCtxRef.current = audioCtx;
          const dest = audioCtx.createMediaStreamDestination();

          const displayAudioTracks = displayStream.getAudioTracks();
          if (displayAudioTracks.length) {
            const displaySource = audioCtx.createMediaStreamSource(
              new MediaStream([displayAudioTracks[0]]),
            );
            displaySource.connect(dest);
          }

          const micAudioTracks = micStream.getAudioTracks();
          if (micAudioTracks.length) {
            const micSource = audioCtx.createMediaStreamSource(
              new MediaStream([micAudioTracks[0]]),
            );
            micSource.connect(dest);
          }

          mixedStream = dest.stream;
        }

        const mr = new MediaRecorder(mixedStream);
        mediaRecorderRef.current = mr;
        activeRecordingInterRef.current = inter;
        recordChunksRef.current = [];

        mr.ondataavailable = (e) =>
          e.data.size && recordChunksRef.current.push(e.data);
        mr.onstop = async () => {
          setRecordingOpen(false);
          const blob = new Blob(recordChunksRef.current, {
            type: "audio/webm",
          });
          recordChunksRef.current = [];
          mixedStream?.getTracks().forEach((t) => t.stop());
          displayStream?.getTracks().forEach((t) => t.stop());
          micStream?.getTracks().forEach((t) => t.stop());
          audioCtx?.close().catch(() => {});
          displayStreamRef.current = null;
          micStreamRef.current = null;
          audioCtxRef.current = null;
          setUploadingFor(intervieweeId);
          setLoading(true);
          await uploadRecordingToCloudinary(blob, inter, intervieweeId);
          setUploadingFor(null);
          setRecordingFor(null);
          setRecordSeconds(0);
          activeRecordingInterRef.current = null;
        };

        mr.start();
        setRecordingFor(intervieweeId);
        setRecordSeconds(0);
        if (recordTimerRef.current) clearInterval(recordTimerRef.current);
        recordTimerRef.current = setInterval(
          () => setRecordSeconds((prev) => prev + 1),
          1000,
        );
      } catch (e) {
        mixedStream?.getTracks().forEach((t) => t.stop());
        displayStream?.getTracks().forEach((t) => t.stop());
        micStream?.getTracks().forEach((t) => t.stop());
        audioCtx?.close().catch(() => {});
        setError(
          e.message ||
            "Could not start recording. Try sharing screen with system audio.",
        );
        setRecordingFor(null);
        setRecordSeconds(0);
      }
    },
    [
      assessment?.assessmentId,
      patchIntervieweeFiles,
      uploadRecordingToCloudinary,
    ],
  );

  const handleRecordClick = (inter) => {
    const id = getIntervieweeKey(inter);
    setRecordingPerson({
      name: inter.name || "Interviewee",
      type: inter.type || "Readiness",
      data: inter,
    });
    setRecordingOpen(true);
    if (recordingFor && recordingFor === id) {
      stopRecording();
    }
  };

  const handleRecordingChoice = useCallback(
    (micOnly) => {
      const target = recordInterRef.current || recordingPerson.data;
      setRecordChoiceOpen(false);
      recordInterRef.current = null;
      if (target) {
        setRecordingPerson((prev) => ({
          name: target.name || prev.name || "Interviewee",
          type: target.type || prev.type || "Readiness",
          data: target,
        }));
        setRecordingOpen(true);
        startRecording(target, micOnly);
      }
    },
    [recordingPerson.data, startRecording],
  );

  const deleteFileConfirmed = useCallback(
    async (inter, fileIdx) => {
      const assessmentIdVal = assessment?.assessmentId;
      const intervieweeId = getIntervieweeKey(inter);
      if (!authToken || !assessmentIdVal || !intervieweeId) {
        setAlertDialog({
          open: true,
          title: "Delete failed",
          message: "Missing authorization or identifiers.",
        });
        return;
      }
      try {
        const currentFiles = Array.isArray(inter.files) ? inter.files : [];
        const nextFiles = currentFiles.filter((_, idx) => idx !== fileIdx);
        setUploadingFor(intervieweeId);
        await patchIntervieweeFiles({
          assessmentId: assessmentIdVal,
          intervieweeId,
          files: nextFiles,
        });
        await fetchAssessment();
        setAlertDialog({
          open: true,
          title: "Removed",
          message: "File removed successfully.",
        });
      } catch (e) {
        setAlertDialog({
          open: true,
          title: "Delete failed",
          message: e.message || "Could not remove the file.",
        });
      } finally {
        setUploadingFor(null);
      }
    },
    [
      assessment?.assessmentId,
      authToken,
      fetchAssessment,
      getIntervieweeKey,
      patchIntervieweeFiles,
      setUploadingFor,
    ],
  );

  const handleDeleteFile = (inter, fileIdx) => {
    const assessmentIdVal = assessment?.assessmentId;
    const intervieweeId = getIntervieweeKey(inter);
    if (!authToken || !assessmentIdVal || !intervieweeId) {
      setAlertDialog({
        open: true,
        title: "Delete failed",
        message: "Missing authorization or identifiers.",
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete file",
      message: "This can't be undone. Remove this file?",
      onConfirm: () => deleteFileConfirmed(inter, fileIdx),
    });
  };

  const startProcessingDialog = useCallback((title) => {
    setProcessingModal({ open: true, seconds: 0, title });
    if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    processingTimerRef.current = setInterval(() => {
      setProcessingModal((prev) => ({ ...prev, seconds: prev.seconds + 1 }));
    }, 1000);
  }, []);

  const stopProcessingDialog = useCallback(() => {
    if (processingTimerRef.current) {
      clearInterval(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    setProcessingModal((prev) => ({ ...prev, open: false, seconds: 0 }));
  }, []);

  const handleSubmitDoc = useCallback(
    async (docTitle) => {
      if (!assessment || !authToken) {
        setError("Not authorized or assessment missing.");
        return;
      }

      const isPolicy = docTitle.toLowerCase().includes("policies");
      const flowId = isPolicy
        ? "f355fc25-6458-47bd-bde1-ea2806720a9d"
        : "07968cba-40fb-4cb9-96a5-ef2dd2b45bc4";

      try {
        setDocStates((prev) => ({ ...prev, [docTitle]: "processing" }));
        // kick off polling timers for this doc type
        const companyName =
          assessment?.company || apiAssessment?.company || "Assessment";
        if (isPolicy) {
          const payload = {
            startedAt: Date.now(),
            companyName,
          };
          setPolicyTimer(payload);
          storeTimer(POLICY_TIMER_KEY, payload);
        } else {
          const payload = {
            startedAt: Date.now(),
            companyName,
          };
          setAssessTimer(payload);
          storeTimer(ASSESS_TIMER_KEY, payload);
        }
        startProcessingDialog(
          isPolicy
            ? "Generating Ai Policy & Guidelines Assessment"
            : "Generating Ai-Assessment",
        );

        const myHeaders = new Headers();
        myHeaders.append(
          "api-key",
          "FLUX_API.74962c2d-51b8-49a2-9592-a0fdb21cb18c",
        );
        myHeaders.append("Content-Type", "application/json");

        const variableInputs = [];
        variableInputs.push({
          inputId: isPolicy
            ? "varInputNode_1771611159441_0.4392"
            : "varInputNode_1775161856796_0.8227",
          inputText: assessment.assessmentId?.toString(),
        });

        const textArr = [];

        interviewees.forEach((iv) => {
          const matchPolicy = iv.type === "Policies";

          if (isPolicy ? matchPolicy : !matchPolicy) {
            (iv.files || []).forEach((f) => {
              if (f?.transcript?.text) {
                textArr.push(f.transcript.text);
              }
            });
          }
        });

        if (isPolicy) {
          variableInputs.push({
            inputId: "varInputNode_1771611169663_0.4963",
            inputText: companyLogo,
          });
        } else {
          variableInputs.push({
            inputId: "varInputNode_1775161866728_0.2418",
            inputText: companyLogo,
          });
        }

        if (isPolicy) {
          variableInputs.push({
            inputId: "varInputNode_1775163199771_0.5294",
            inputText: textArr.join("$$$$$"),
          });
        } else {
          variableInputs.push({
            inputId: "varInputNode_1775161870538_0.8663",
            inputText: textArr.join("$$$$$"),
          });
        }

        const raw = JSON.stringify({ variableInputs });
        const response = await fetch(
          `https://api.fluxprompt.ai/flux/api-v2?flowId=${flowId}&sessionId=${assessment.assessmentId?.toString()}`,
          { method: "POST", headers: myHeaders, body: raw, redirect: "follow" },
        );

        if (!response.ok) throw new Error("FluxPrompt API error");
        const fluxJson = await response.json();
        const pdfMessage = fluxJson?.data?.message?.[0];
        const pdfUrl = pdfMessage?.text;
        setAlertDialog({
          open: true,
          title: "Success",
          message:
            "AI assessment generation is in progress and will be added for download when ready.",
        });
      } catch (e) {
        setError(e.message || "Failed to generate document.");
        setDocStates((prev) => ({ ...prev, [docTitle]: "idle" }));
      } finally {
        stopProcessingDialog();
      }
    },
    [
      assessment,
      authToken,
      interviewees,
      startProcessingDialog,
      stopProcessingDialog,
    ],
  );

  const deleteAssessmentDocumentConfirmed = useCallback(
    async (kind) => {
      const assessmentId = assessment?.assessmentId;
      if (!assessmentId || !authToken) {
        setAlertDialog({
          open: true,
          title: "Unable to delete",
          message: "Missing assessment or authorization info.",
        });
        return;
      }

      try {
        setLoading(true);
        const body =
          kind === "policy"
            ? { assessmentId, assessmentAiPolicyPdf: "" }
            : { assessmentId, assessmentFile: "" };

        const res = await fetch(ASSESSMENT_URL, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            authorization: authToken,
          },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Delete failed");
        }

        setApiAssessment((prev) =>
          prev
            ? {
                ...prev,
                ...(kind === "policy"
                  ? { assessmentAiPolicyPdf: "" }
                  : { assessmentFile: "" }),
              }
            : prev,
        );
        setLoading(false);
        setAlertDialog({
          open: true,
          title: "Removed",
          message: "File removed successfully.",
        });
      } catch (e) {
        setAlertDialog({
          open: true,
          title: "Delete failed",
          message: e.message || "Could not remove the file.",
        });
      } finally {
        setLoading(false);
      }
    },
    [assessment, authToken],
  );

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
      }
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <FullScreenLoader />
      </DashboardLayout>
    );
  }

  if (!assessment) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto py-12 text-center">
          <p className="text-muted-foreground">
            {error || "Assessment not found."}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/assessments")}
          >
            {t("Back to Assessments")}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const completedWithTranscripts = interviewees.filter(
    (i) => i.interview === "completed" && i.transcript,
  ).length;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 -ml-2"
          onClick={() => {
            if (recordingFor) {
              stopRecording();
            }
            navigate("/assessments");
          }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t("Back to Assessments")}
        </Button>

        {/* Company Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold font-display tracking-tight text-foreground">
              {assessment.company}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {assessment.contact}
              {assessment.contactTitle ? ` ${assessment.contactTitle}` : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              {assessment.email} {assessment.phone}
            </p>
          </div>
          <Badge className="bg-green-500 hover:bg-green-500/80 text-white border-transparent text-sm px-4 py-1">
            {getStatusLabel(assessment.status)}
          </Badge>
        </div>

        {/* Overall Progress */}
        <Card className="shadow-[var(--card-shadow)]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold font-display">
                {t("Overall Progress")}
              </span>
              <span className="text-sm font-semibold text-muted-foreground">
                {assessment.progress}%
              </span>
            </div>
            <Progress value={assessment.progress} className="h-3" />
            <p className="text-xs text-muted-foreground mt-2">
              {completedWithTranscripts}/{interviewees.length}{" "}
              {t("completed with transcripts")}
            </p>
          </CardContent>
        </Card>

        {/* Interviewees */}
        <Card className="shadow-[var(--card-shadow)]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-display">
              {t("Interviewees")}
            </CardTitle>
            {!aiAssessmentCompleted && !aiPolicyAssessmentCompleted && (
              <Button
                size="sm"
                className="gap-1"
                onClick={() => setAddOpen(true)}
              >
                <Plus className="h-4 w-4" />
                {t("Add")}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Name")}</TableHead>
                  <TableHead>{t("Role")}</TableHead>
                  <TableHead>{t("Type")}</TableHead>
                  <TableHead>{t("Interview")}</TableHead>
                  <TableHead>{t("Files / Transcript")}</TableHead>
                  <TableHead>{t("Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interviewees.map((person, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{person.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {person.role}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {person.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={getInterviewBadgeClass(person.interview)}
                      >
                        {person.interview}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(person.files) &&
                      person.files.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {person.files.map((file, idx) => (
                            <div
                              key={`${file.public_id || file.url || idx}`}
                              className="flex items-center gap-1"
                            >
                              {file ? (
                                <Badge
                                  variant="secondary"
                                  className="flex items-center gap-1 text-[11px] px-2 py-1"
                                >
                                  <button
                                    type="button"
                                    className="flex items-center gap-1"
                                  >
                                    File {idx + 1}
                                    <File
                                      className="h-3 w-3 cursor-pointer"
                                      onClick={() =>
                                        setTranscriptDialog({
                                          open: true,
                                          text: file.transcript.text,
                                        })
                                      }
                                    />
                                    <Eye
                                      className="h-3.5 w-3.5 cursor-pointer"
                                      onClick={() =>
                                        window.open(file.url, "_blank")
                                      }
                                    />
                                    {((person.type !== "Policies" &&
                                      !aiAssessmentCompleted) ||
                                      (person.type === "Policies" &&
                                        !aiPolicyAssessmentCompleted)) && (
                                      <Trash
                                        className="h-3 w-3 text-red-500 cursor-pointer"
                                        onClick={() => {
                                          handleDeleteFile(person, idx);
                                        }}
                                      />
                                    )}
                                  </button>
                                </Badge>
                              ) : (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px]"
                                >
                                  No transcript
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Not uploaded
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {/* <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => handleRecordClick(person)}
                          disabled={
                            uploadingFor ===
                            (person.intervieweeId || person._id || person.id)
                          }
                        >
                          <Mic className="h-3.5 w-3.5" />
                          Record
                        </Button> */}

                        {((person.type !== "Policies" &&
                          !aiAssessmentCompleted) ||
                          (person.type === "Policies" &&
                            !aiPolicyAssessmentCompleted)) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            disabled={
                              uploadingFor ===
                              (person.intervieweeId || person._id || person.id)
                            }
                            onClick={() => handleUploadPdf(person)}
                          >
                            <Upload className="h-3.5 w-3.5" />
                            {uploadingFor ===
                            (person.intervieweeId || person._id || person.id)
                              ? "Uploading..."
                              : "Upload"}
                          </Button>
                        )}

                        {/* <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => {
                            navigate("/calendar", {
                              state: {
                                assessmentId: assessment.assessmentId,
                                intervieweeId:
                                  person.intervieweeId ||
                                  person._id ||
                                  person.id,
                              },
                            });
                          }}
                        >
                          <CalendarDays className="h-3.5 w-3.5" />
                          Schedule
                        </Button> */}

                        {((person.type !== "Policies" &&
                          !aiAssessmentCompleted) ||
                          (person.type === "Policies" &&
                            !aiPolicyAssessmentCompleted)) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => {
                              setEditInterviewee(person);
                              setEditName(person.name || "");
                              setEditPosition(person.role || "");
                              setEditPolicies(
                                person.type === "Policies" ? "Yes" : "No",
                              );
                              setEditOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {((person.type !== "Policies" &&
                          !aiAssessmentCompleted) ||
                          (person.type === "Policies" &&
                            !aiPolicyAssessmentCompleted)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                            disabled={
                              deletingId ===
                              (person.intervieweeId || person._id || person.id)
                            }
                            onClick={() => {
                              handleDeleteInterviewee(person);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Output Documents */}
        <Card className="shadow-[var(--card-shadow)]">
          <CardHeader>
            <CardTitle className="text-lg font-display">
              {t("Documents")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {getOutputDocuments(interviewees).map((doc, idx) => {
              const state = docStates[doc.title] || "idle";
              const docKind =
                doc.title === "AI Policies & Guidelines"
                  ? "policy"
                  : "assessment";
              const downloadLink =
                docKind === "policy"
                  ? assessment?.assessmentAiPolicyPdf
                  : assessment?.assessmentFile;

              return (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-3 rounded-lg border p-4"
                >
                  <div>
                    <p className="font-medium text-sm">{t(doc.title)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {downloadLink
                        ? t("Document ready for download")
                        : state === "processing"
                          ? docKind === "policy" && policyTimer?.startedAt
                            ? `${t("Processing…")} ${policyElapsed.minutes}m ${
                                policyElapsed.seconds
                              }s`
                            : docKind === "assessment" && assessTimer?.startedAt
                              ? `${t("Processing…")} ${
                                  assessElapsed.minutes
                                }m ${assessElapsed.seconds}s`
                              : t("Processing… This may take a few minutes")
                          : getDocumentSubtitle(doc)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 relative">
                    {downloadLink ? (
                      <>
                        <Button
                          size="sm"
                          className="gap-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                          onClick={() =>
                            setAssetMenuOpen((prev) =>
                              prev === idx ? null : idx,
                            )
                          }
                        >
                          <Download className="h-3.5 w-3.5" />
                          {t("Download Assets")}
                        </Button>

                        {assetMenuOpen === idx && (
                          <div className="absolute right-0 top-full mt-2 w-48 bg-popover text-popover-foreground border rounded shadow-lg z-50">
                            <div className="flex flex-col py-1">
                              {(() => {
                                const list = [];
                                if (
                                  assessment?.assessmentAiPolicyPdf &&
                                  docKind === "policy"
                                ) {
                                  list.push({
                                    label: t("AI Policies & Guidelines"),
                                    url: assessment.assessmentAiPolicyPdf,
                                  });
                                }
                                if (assessment?.assessmentFile) {
                                  list.push({
                                    label: t("PDF File"),
                                    url: assessment.assessmentFile,
                                  });
                                }
                                if (assessment?.assessmentDocxFile) {
                                  list.push({
                                    label: t("DOCX File"),
                                    url: assessment?.assessmentDocxFile,
                                  });
                                }
                                if (assessment?.assessmentPptxFile) {
                                  list.push({
                                    label: t("PPTX File"),
                                    url: assessment?.assessmentPptxFile,
                                  });
                                }
                                if (list.length === 0 && downloadLink) {
                                  list.push({
                                    label: t("Download"),
                                    url: downloadLink,
                                  });
                                }

                                return list.map((it, i) => (
                                  <a
                                    key={i}
                                    href={it.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block px-3 py-2 text-sm hover:bg-accent/5"
                                    onClick={() => setAssetMenuOpen(null)}
                                  >
                                    {it.label}
                                  </a>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        disabled={
                          state === "processing" ||
                          (doc.title === "AI Policies & Guidelines"
                            ? !allHaveFilesPolicy
                            : !allHaveFilesReadiness)
                        }
                        className="gap-1 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
                        onClick={() => handleSubmitDoc(doc.title)}
                      >
                        {state === "processing" && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        )}
                        {state === "processing"
                          ? t("Processing…")
                          : t("Submit")}
                      </Button>
                    )}
                    {!(
                      doc.title === "AI Workflow Assessment" &&
                      aiAssessmentCompleted
                    ) &&
                    !(
                      doc.title === "AI Policies & Guidelines" &&
                      aiPolicyAssessmentCompleted
                    ) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive border-destructive/40 hover:text-destructive"
                        disabled={!downloadLink && state !== "ready"}
                        onClick={() =>
                          setConfirmDialog({
                            open: true,
                            title: t("Delete Document"),
                            message: `${t(
                              "assessment.deleteDocumentConfirmPrefix",
                            )} ${t(
                              docKind === "policy"
                                ? "AI Policies & Guidelines"
                                : "AI Readiness Assessment",
                            )} ${t("assessment.deleteDocumentConfirmSuffix")}`,
                            onConfirm: () =>
                              deleteAssessmentDocumentConfirmed(docKind),
                          })
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {t("Delete")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="text-center text-xs text-muted-foreground py-4 border-t">
          © {new Date().getFullYear()} GoWest.ai — All Rights Reserved ·{" "}
          <a
            href="mailto:support@bizvantage.ai"
            className="text-accent hover:underline"
          >
            support@bizvantage.ai
          </a>
        </footer>
      </div>
      <ScheduleInterviewDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        assessment={assessment}
        defaultInterviewee={scheduleInterviewee}
      />
      {assessment && (
        <AddIntervieweeDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onAdd={handleAddInterviewees}
        />
      )}
      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditInterviewee(null);
          }
          setEditOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("Edit Interviewee")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-muted-foreground">
                {t("Name")}
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-200 bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground">
                {t("form.position")}
              </label>
              <input
                type="text"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-200 bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground">
                {t("AI Policies & Guidelines")}
              </label>
              <select
                value={editPolicies}
                onChange={(e) => setEditPolicies(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-200 bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="Yes">{t("Yes")}</option>
                <option value="No">{t("No")}</option>
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleSaveEditInterviewee} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RecordingDialog
        open={recordingOpen}
        onOpenChange={setRecordingOpen}
        intervieweeName={recordingPerson.name}
        intervieweeType={recordingPerson.type}
        onStartRecording={() => {
          if (recordingPerson.data) {
            recordInterRef.current = recordingPerson.data;
            setRecordChoiceOpen(true);
          }
        }}
        onStopRecording={() => stopRecording()}
        isRecording={!!isRecordingActive}
        elapsedSeconds={elapsedForDialog}
      />

      <Dialog
        open={recordChoiceOpen}
        onOpenChange={(open) => {
          if (!open) {
            recordInterRef.current = null;
          }
          setRecordChoiceOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("Choose recording mode")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t(
              "Record only your microphone, or include meeting/system audio by sharing a tab/window with system audio enabled.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleRecordingChoice(true)}
            >
              {t("Mic only")}
            </Button>
            <Button
              className="flex-1"
              onClick={() => handleRecordingChoice(false)}
            >
              {t("Meeting audio + Mic")}
            </Button>
          </div>
          <div className="flex justify-end mt-3">
            <Button
              variant="ghost"
              onClick={() => {
                recordInterRef.current = null;
                setRecordChoiceOpen(false);
              }}
            >
              {t("Cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MiniDialog
        open={transcriptDialog.open}
        onOpenChange={(open) =>
          setTranscriptDialog((prev) => ({ ...prev, open }))
        }
      >
        <MiniDialogContent className="max-w-2xl">
          <MiniDialogHeader>
            <MiniDialogTitle className="font-display">
              {t("Transcript")}
            </MiniDialogTitle>
          </MiniDialogHeader>
          <div className="mt-4 max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm text-muted-foreground">
            {transcriptDialog.text || t("No transcript available.")}
          </div>
        </MiniDialogContent>
      </MiniDialog>

      <MiniDialog
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog((prev) => ({ ...prev, open }))}
      >
        <MiniDialogContent className="max-w-md">
          <MiniDialogHeader>
            <MiniDialogTitle className="font-display">
              {alertDialog.title || t("Notice")}
            </MiniDialogTitle>
          </MiniDialogHeader>
          <div className="mt-4 text-sm text-muted-foreground">
            {alertDialog.message || t("Notification")}
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={() =>
                setAlertDialog((prev) => ({ ...prev, open: false }))
              }
            >
              {t("Close")}
            </Button>
          </div>
        </MiniDialogContent>
      </MiniDialog>

      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) closeConfirmDialog();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {confirmDialog.title || t("Confirm")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 text-sm text-muted-foreground">
            {confirmDialog.message ||
              t(
                "Are you sure you want to proceed? This action cannot be undone.",
              )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeConfirmDialog}>
              {t("Cancel")}
            </Button>
            <Button variant="destructive" onClick={handleConfirmAction}>
              {t("Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={processingModal.open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {processingModal.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            <p>{t("Generating document… please wait.")}</p>
            <p className="mt-2 font-mono">
              {t("Elapsed")}: {Math.floor(processingModal.seconds / 60)}m{" "}
              {processingModal.seconds % 60}s
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AssessmentNewV1;
